import { TWidget } from 'widgets/Widget';

const keyCode = Object.freeze({
    RETURN: 13,
    SPACE: 32
});

/**
 * @param Widget Base widget for extending
 * @returns EmitBusEvent widget
 */
function EmitBusEventClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory global
     * @class EmitBusEvent
     * @augments Widget
     * @classdesc Could be used to trigger any global event using interaction element
     * <br>Could be useful to emit global events like `page.show.globalmodal` to notify
     * <br>concerned widgets, so they could change page layout class etc.
     * <br>Event emission could happens mostly on UI elements like links, buttons etc.
     * @property {string} data-widget - Widget name `emitBusEvent`
     * @property {string} data-bus-event-type - Global event type, which should be triggered
     * @property {string} data-event-click - Event listener to trigger `emitBusEvent` method
     * @property {string} data-url - any kind of additional properties
     *
     * @example <caption>Example of EmitBusEvent widget usage</caption>
     * <button
     *     data-widget="emitBusEvent"
     *     data-bus-event-type="dialogshow"
     *     data-event-click.prevent="emitBusEvent"
     *     data-event-keydown="handleKeydown"
     *     data-modal-config='{
     *         "url": "${URLUtils.url('Page-LocaleContent')}",
     *         "headerText": "${Resource.msg('form.countrySelector.title', 'forms', null)}"
     *     }'
     * >
     *     ... content
     * </button>
     */
    class EmitBusEvent extends Widget {
        prefs() {
            return {
                busEventType: '',
                ...super.prefs()
            };
        }

        /**
         * @description Emit event
         */
        emitBusEvent() {
            if (this.prefs().busEventType) {
                this.eventBus().emit(this.prefs().busEventType, this);
            }
        }

        /**
         * @description Keydown Event handler
         * Handles keydown events and emits a bus event when the RETURN or SPACE key is pressed.
         * @listens dom#keydown
         * @param _ Source of keydown event
         * @param event  Event object
         */
        handleKeydown(_: HTMLElement, event: KeyboardEvent) {
            if (event.keyCode === keyCode.RETURN || event.keyCode === keyCode.SPACE) {
                this.emitBusEvent();
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    return EmitBusEvent;
}

export type TEmitBusEvent = ReturnType<typeof EmitBusEventClassCreator>;

export type TEmitBusEventInstance = InstanceType<TEmitBusEvent>;

export default EmitBusEventClassCreator;
